import { capitalize } from '@mui/material/utils';

import { IconButton } from '../../../button';
import { type FlexProps, Flex } from '../../../flex';
import { styled, ColorPalette as DraggableColor } from '../../../theme';

enum DraggableVariant {
  FLAT = 'flat',
  OUTLINED = 'outlined',
}

enum DraggableBehavior {
  DRAG_WITHOUT_HANDLER = 'DRAG_WITHOUT_HANDLER',
  DRAG_WITH_HANDLER = 'DRAG_WITH_HANDLER',
  DRAG_DISABLED = 'DRAG_DISABLED',
}

interface DraggableBaseProps extends Omit<FlexProps, 'ref'> {
  color?: `${DraggableColor}`;
  variant?: `${DraggableVariant}`;
  behavior?: `${DraggableBehavior}`;
  disableGutters?: boolean;
}

const DRAGGABLE_CLICK_BEHAVIOR: DraggableBehavior[] = [
  DraggableBehavior.DRAG_WITH_HANDLER,
  DraggableBehavior.DRAG_WITHOUT_HANDLER,
];

type DraggableOptionProps = Pick<
  DraggableBaseProps,
  'color' | 'variant' | 'disableGutters' | 'behavior'
>;

const DraggableRoot = styled(Flex, {
  name: 'Draggable',
  slot: 'Root',
  shouldForwardProp: (prop) =>
    prop !== 'color' &&
    prop !== 'variant' &&
    prop !== 'behavior' &&
    prop !== 'disableGutters' &&
    prop !== 'isDragging',
  overridesResolver: ({ theme, color: colorProp, variant: variantProp }, styles) => {
    const defaultProps = theme.components?.['Draggable']?.defaultProps;

    const color = colorProp ?? defaultProps?.color ?? DraggableColor.PRIMARY;
    const variant = variantProp ?? defaultProps?.variant ?? DraggableVariant.FLAT;

    return [styles['root'], styles[variant], styles[`${variant}${capitalize(color)}`]];
  },
})<
  DraggableBaseProps & {
    isDragging: boolean;
  }
>(({
  theme,
  color: colorProps,
  variant: variantProps,
  behavior: behaviorProps = DraggableBehavior.DRAG_WITH_HANDLER,
  isDragging,
  disableGutters,
}) => {
  const defaultProps = theme.components?.['Draggable']?.defaultProps;

  const color = colorProps ?? defaultProps?.color ?? DraggableColor.PRIMARY;
  const variant = variantProps ?? defaultProps?.variant ?? DraggableVariant.FLAT;

  const behavior: DraggableBehavior =
    (behaviorProps as DraggableBehavior) ??
    defaultProps?.behavior ??
    DraggableBehavior.DRAG_WITH_HANDLER;

  const isClickable = DRAGGABLE_CLICK_BEHAVIOR.includes(behavior);

  return {
    flexDirection: 'column',
    backgroundColor: 'white',
    '& > .MuiBox-root:first-of-type': {
      paddingBlock: disableGutters ? 0 : theme.spacing(1),
      paddingInline: theme.spacing(2),
    },
    ...(variant === DraggableVariant.OUTLINED
      ? {
          border: '1px solid',
          borderColor: theme.palette.grey[400],
          borderRadius: 8,
        }
      : null),
    ...(variant === DraggableVariant.OUTLINED &&
      (isDragging
        ? {
            backgroundColor: isClickable ? theme.palette.grey[50] : 'white',
            boxShadow: '0px 4px 16px rgba(52, 58, 64, 0.1)',
          }
        : {
            '&:hover': {
              backgroundColor: isClickable ? theme.palette.grey[50] : 'white',
            },
            '&:has(.Draggable-root)': {
              backgroundColor: 'white',
            },
          })),
    ...(variant === DraggableVariant.FLAT &&
      (isDragging
        ? {
            boxShadow: '0px 4px 16px rgba(52, 58, 64, 0.1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              width: 3,
              height: '100%',
              backgroundColor: theme.palette[color].main,
            },
          }
        : {
            '&:hover': {
              backgroundColor: isClickable ? theme.palette.grey[50] : 'white',
            },
            '&:has(.Draggable-root)': {
              backgroundColor: 'white',
            },
          })),
  };
});

const DraggableHandlerIcon = styled(IconButton, {
  name: 'Draggable',
  slot: 'DragIcon',
  overridesResolver: (props, styles) => {
    return [styles['dragIcon']];
  },
})<DraggableBaseProps>(({ theme }) => ({
  marginRight: theme.spacing(1),
  cursor: 'grab',
  '.MuiSvgIcon-root': { fontSize: 16, color: theme.palette.grey[900] },
}));

export { DraggableBehavior, DraggableRoot, DraggableHandlerIcon, DraggableColor, DraggableVariant };
export type { DraggableBaseProps, DraggableOptionProps };
export default DraggableRoot;
